import { useEffect, useRef, useState } from 'preact/hooks';
import { DOUBLE_TAP_ZOOM, KEYBOARD_ZOOM_STEP, TOAST_MS } from '../config.js';
import { formatAge, formatFrameDateTime } from '../format.js';
import { type PlayerController } from '../player/controller.js';
import { CanvasRenderer } from '../render/canvas-renderer.js';
import { fitRect, isDefaultView, panBy, zoomAt } from '../render/view-transform.js';
import {
  currentFrame,
  error,
  isStale,
  latestAgeSeconds,
  loadProgress,
  loadedCount,
  loading,
  resetView,
  view,
} from '../state/store.js';
import { DownloadMenu } from './download-menu.js';
import { attachGestures } from './gestures.js';
import { attachKeyboard } from './keyboard.js';

type Props = {
  player: PlayerController;
};

const PERCENT = 100;

export const Viewer = ({ player }: Props) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<CanvasRenderer | null>(null);
  const frame = currentFrame.value;
  const layers = player.layersFor(frame);
  const layerKey = layers.map(layer => layer === null ? '-' : '+').join('');
  const loadedTick = loadedCount.value;
  const currentView = view.value;
  const [ toast, setToast ] = useState<string | null>(null);

  const notify = (message: string): void => {
    setToast(message);
    setTimeout(() => setToast(null), TOAST_MS);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) {
      return undefined;
    }
    rendererRef.current ??= new CanvasRenderer(canvas);
    const renderer = rendererRef.current;
    const redraw = (): void => {
      renderer.resize(canvas.clientWidth, canvas.clientHeight, globalThis.devicePixelRatio || 1);
      renderer.draw(player.layersFor(currentFrame.value), view.value);
    };
    redraw();
    const observer = new ResizeObserver(redraw);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [ player, frame, layerKey, loadedTick, currentView ]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) {
      return undefined;
    }
    const geometry = () => {
      const reference = player.layersFor(currentFrame.value).find(layer => layer !== null);
      const renderer = rendererRef.current;
      if (reference == null || renderer === null) {
        return null;
      }
      const viewport = renderer.viewportSize();
      return { viewport, fit: fitRect(viewport, reference) };
    };
    const detachKeyboard = attachKeyboard(window, {
      player,
      zoomIn: () => {
        const geo = geometry();
        if (geo !== null) {
          const center = { x: geo.viewport.width / 2, y: geo.viewport.height / 2 };
          view.value = zoomAt(view.value, geo.fit, geo.viewport, KEYBOARD_ZOOM_STEP, center);
        }
      },
      zoomOut: () => {
        const geo = geometry();
        if (geo !== null) {
          const center = { x: geo.viewport.width / 2, y: geo.viewport.height / 2 };
          view.value = zoomAt(view.value, geo.fit, geo.viewport, 1 / KEYBOARD_ZOOM_STEP, center);
        }
      },
      zoomReset: () => resetView(),
    });
    const detachGestures = attachGestures(canvas, {
      pan: (dx, dy) => {
        const geo = geometry();
        if (geo !== null && !isDefaultView(view.value)) {
          view.value = panBy(view.value, geo.fit, geo.viewport, dx, dy);
        }
      },
      zoom: (factor, x, y) => {
        const geo = geometry();
        if (geo !== null) {
          view.value = zoomAt(view.value, geo.fit, geo.viewport, factor, { x, y });
        }
      },
      doubleTap: (x, y) => {
        const geo = geometry();
        if (geo === null) {
          return;
        }
        view.value = isDefaultView(view.value)
          ? zoomAt(view.value, geo.fit, geo.viewport, DOUBLE_TAP_ZOOM, { x, y })
          : { ...view.value, scale: 1 };
        if (!isDefaultView(view.value)) {
          return;
        }
        resetView();
      },
    });
    return () => {
      detachKeyboard();
      detachGestures();
    };
  }, [ player ]);

  return (
    <main class="viewer">
      <canvas ref={canvasRef} class="viewer__canvas" aria-label="Imagen del radar" />
      {frame !== null && (
        <div class="viewer__label" aria-live="polite">
          <strong>{formatFrameDateTime(frame.ts)}</strong>
        </div>
      )}
      <div class="viewer__actions">
        {!isDefaultView(currentView) && (
          <button type="button" class="viewer__action" onClick={() => resetView()} aria-label="Restablecer zoom">
            {currentView.scale.toFixed(1)}× ⤢
          </button>
        )}
        <DownloadMenu player={player} onError={notify} />
      </div>
      {toast !== null && <div class="viewer__toast" role="status">{toast}</div>}
      {isStale.value && !loading.value && (
        <div class="viewer__banner viewer__banner--warn" role="status">
          Última imagen {formatAge(latestAgeSeconds.value)} · el radar o el recolector pueden estar parados
        </div>
      )}
      {error.value !== null && (
        <div class="viewer__banner viewer__banner--error" role="alert">{error.value}</div>
      )}
      {(loading.value || loadProgress.value < 1) && (
        <div class="viewer__progress" role="progressbar" aria-valuenow={Math.round(loadProgress.value * PERCENT)}>
          <div class="viewer__progress-bar" style={{ width: `${loadProgress.value * PERCENT}%` }} />
        </div>
      )}
      {frame === null && !loading.value && error.value === null && (
        <div class="viewer__empty">Sin imágenes todavía</div>
      )}
    </main>
  );
};
